import { asWidgetProps } from 'Src/types/widget-helpers';
import React, { ComponentProps } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { ICONS } from 'Components/utils/icon/icons';
import ratios from 'Components/utils/ratios/ratios.utils';
import { t } from 'Src/i18n';
import { Image } from 'Components/frameworks/image/Image';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Slider } from 'Components/frameworks/slider/Slider';
import type {
  ComparatorColumn,
  ComparatorAvailabilityBoard,
  ComparatorAllAvailableItems,
} from './ProductRoomsComparator.utils';
import { ITEMS_ORDER, SECTION_LABELS, sortCategoriesItems } from './ProductRoomsComparator.utils';
type Props = {
  allCategoriesItems: ComparatorAllAvailableItems;
  availabilityBoards: Array<ComparatorAvailabilityBoard>;
  columns: Array<ComparatorColumn>;
};
export const ProductRoomsComparatorNarrow = ({
  allCategoriesItems,
  availabilityBoards,
  columns,
}: Props) => {
  const {
    thColumn,
    thTitle,
    thInfoTitle,
    thInfo,
    thSectionHeader,
    thSectionTitle,
    thIcon,
    thCheck,
  } = {
    thColumn: 'bg-white border-1 border-lightGrey rounded-12 shadow-diffuse',
    thTitle: 'p-15 leading-tight font-brand text-20 text-black',
    thInfoTitle: 'flex items-center pt-10 text-mediumGrey text-14',
    thInfo: 'ms-auto font-bold text-16',
    thSectionHeader: 'flex',
    thSectionTitle: 'font-bold text-18 text-black',
    thIcon: 'text-black shrink-0',
    thCheck: 'flex items-center justify-center',
  };

  const getItemsAvailability = (itemType, columnIndex) =>
    allCategoriesItems[ITEMS_ORDER.indexOf(itemType)]
      .sort(
        sortCategoriesItems as unknown as (
          a: { id: string; label: string },
          b: { id: string; label: string },
        ) => number,
      )
      .map((item, index) => {
        return (
          <div key={index} className="flex items-baseline justify-between">
            <div className="flex items-center gap-15 pt-20 text-16">
              <Icon
                {...asWidgetProps<ComponentProps<typeof Icon>>({
                  alt: '',
                  className: thIcon,
                  name: ICONS[item.id] || ICONS.TRIDENT1,
                  width: '1.25rem',
                })}
              />

              {item.label}
            </div>
            <div className={classnames(thCheck)}>
              <Icon
                {...asWidgetProps<ComponentProps<typeof Icon>>({
                  className: availabilityBoards[columnIndex][itemType][index]
                    ? 'text-green'
                    : 'text-grey',
                  name: availabilityBoards[columnIndex][itemType][index]
                    ? ICONS.CHECK
                    : ICONS.CROSS,
                  width: '1rem',
                })}
              />
            </div>
          </div>
        );
      });

  return (
    <div className="mx-auto max-w-site px-20 pt-30">
      <Slider
        {...asWidgetProps<ComponentProps<typeof Slider>>({
          cols: 0,
          navPosition: 'center',
          nbItemsToScroll: 1,
          scrollAreaClassName: 'overflow-hidden -mx-10',
          scrollAreaContainerClassName:
            'overflow-auto scroll-x scrollbar-hidden snap-x snap-mandatory',
          scrollItemClassName: 'overflow-hidden px-10 pb-20 flex-none max-w-full',
        })}
      >
        {columns.map((column, columnIndex) => (
          <div key={columnIndex} className={classnames(thColumn, 'w-[80vw]')}>
            <div className={thTitle}>
              {column.title}
              {column.price && <div className="font-sans">{column.price}</div>}
            </div>
            <Image
              {...asWidgetProps<ComponentProps<typeof Image>>({
                altMsg: column.title,
                hasLazyLoading: true,
                imageUrl: column.picture?.url,
                ratioKey: ratios.XL,
              })}
            />

            <p className="flex items-baseline gap-5 px-15">
              <span className={thInfoTitle}>{t('common.accommodations.size')}</span>
              <span className={thInfo}>{column.surface}</span>
            </p>
            <p className="flex items-baseline gap-5 px-15">
              <span className={thInfoTitle}>{t('common.accommodations.capacity')}</span>
              <span className={thInfo}>{column.capacity}</span>
            </p>
            {column.strengths.length > 0 && (
              <div className="px-15 pb-15 pt-10">
                <div className={thInfoTitle}>{t('common.accommodations.strengths')}</div>
                {column.strengths.map((strength) => (
                  <div key={strength.id} className="flex items-center gap-5 pt-5">
                    <Icon
                      {...asWidgetProps<ComponentProps<typeof Icon>>({
                        className: 'text-black',
                        name: ICONS[strength.id],
                        width: '1rem',
                      })}
                    />
                    {strength.label}
                  </div>
                ))}
              </div>
            )}
            {ITEMS_ORDER.map(
              (itemType, itemIndex) =>
                !!allCategoriesItems[ITEMS_ORDER.indexOf(itemType)].length && (
                  <div
                    key={itemIndex}
                    className={classnames('border-t-1 border-lightGrey px-15 pb-30 pt-20', {
                      'mt-10': itemIndex === 0,
                    })}
                  >
                    <div className={thSectionHeader}>
                      <div className={thSectionTitle}>{t(SECTION_LABELS[itemType])}</div>
                    </div>
                    {getItemsAvailability(itemType, columnIndex)}
                  </div>
                ),
            )}
          </div>
        ))}
      </Slider>
    </div>
  );
};
